import { useEffect, useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import clsx from 'clsx';
import { useDownloadStatementsStore, useGetStatementsStore } from '@lib/store';
import { useAccountContext, useResponsive } from '@shared/hooks';
import { PaletteUtils } from '@shared/utils';
import { MobileHeader } from '@widgets';
import { Empty, Spinner, Table, Tabs } from '@shared/widgets';
import DownloadIcon from '@assets/icons/cards/download.svg';
import DocumentIcon from '@assets/icons/cards/document.svg';
import StatementIcon from '@assets/icons/cards/statements.svg';

export const Statements = () => {
  const statementsStore = useGetStatementsStore();
  const downloadStatementsStore = useDownloadStatementsStore();

  const [activeKey, setActiveKey] = useState<string | number>('');

  const { account } = useAccountContext();
  const { isMobile } = useResponsive();
  const { t } = useTranslation('card');

  useEffect(() => {
    if (account) statementsStore.get(account.id);
  }, [account]);

  const years = useMemo(() => {
    if (!statementsStore.data || !statementsStore.data.length) return [];
    return [...new Set(statementsStore.data.map((item) => item.year))].sort((a, b) => b - a);
  }, [statementsStore.data]);

  useEffect(() => {
    if (years.length > 0 && !activeKey) setActiveKey(years[0]);
  }, [years, activeKey]);

  const handleChangeYear = (year: string | number) => setActiveKey(year as string);
  const handleDownload = (e: React.MouseEvent, record: any) => {
    e.preventDefault();
    downloadStatementsStore.downloadStatements(record);
  };

  const currentYear = activeKey || years[0];
  const tabOptions = useMemo(() => years.map((year) => ({ value: year, title: year.toString() })), [years]);
  const tabOptionsMobile = useMemo(() => years.map((year) => ({ value: year, label: year.toString() })), [years]);
  const filteredStatements = useMemo(() => {
    if (!statementsStore.data || !currentYear) return [];
    return statementsStore.data
      .filter((item) => item.year === currentYear)
      .map((item) => ({ ...item, month: t(`months.${item.month.toLowerCase()}`, { ns: 'common' }) }));
  }, [statementsStore.data, currentYear, t]);
  return (
    <>
      <MobileHeader years={tabOptionsMobile} value={currentYear?.toString()} onChange={handleChangeYear} />
      <div className='relative py-2.5 px-10 max-[840px]:p-0'>
        {statementsStore.loading && <Spinner className='min-h-[200px]' dark />}
        {!statementsStore.data || years.length === 0 ? (
          statementsStore.loading ? null : (
            <Empty icon={<StatementIcon />} title={t('statements.empty.title')} text={t('statements.empty.description')} />
          )
        ) : (
          <div>
            {!isMobile && <Tabs className='!h-15' classNameCaption='!py-0' options={tabOptions} value={currentYear} onChange={handleChangeYear} />}
            <Table
              className='mt-15 max-[840px]:mt-0'
              isMobile={isMobile}
              title={isMobile ? '' : t('statements.title', { year: currentYear })}
              emptyText={
                statementsStore.loading ? null : (
                  <Empty icon={<StatementIcon />} title={t('statements.empty.title')} text={t('statements.empty.description')} />
                )
              }
              columns={[
                {
                  dataIndex: 'name',
                  className: 'max-[840px]:border-r max-[840px]:border-r-[#e5e5e5]',
                  width: '84%',
                  mobile: isMobile,
                  render: (_, { record }) => (
                    <div className='flex  uppercase '>
                      <DocumentIcon className={clsx('min-w-[20px] mr-[4px]', PaletteUtils.isBordierPalette ? 'text-[#dc291c]' : 'text-[#E5C496]')} />
                      <span>{record.month}</span>
                    </div>
                  ),
                },
                {
                  dataIndex: 'download',
                  width: '20%',
                  render: (_, { record }) => (
                    <a
                      className={clsx('flex cursor-pointer', PaletteUtils.isBordierPalette ? 'text-[#dc291c]' : 'text-[#BE8B45]')}
                      href='#'
                      onClick={(e) => handleDownload(e, record)}
                    >
                      <span className='text-[15px] font-medium underline'>{t('statements.columns.download')}</span>
                      <DownloadIcon />
                    </a>
                  ),
                },
                {
                  dataIndex: 'mobileDownload',
                  className: 'border-[#bcb5b94d]',
                  width: 60,
                  mobile: true,
                  render: (_, { record }) => (
                    <a className='flex justify-center uppercase text-[#27272b]' href='#' onClick={(e) => handleDownload(e, record)}>
                      <DownloadIcon className={clsx('min-w-[20px]  mr-[4px]', PaletteUtils.isBordierPalette ? 'text-[#dc291c]' : 'text-[#BE8B45]')} />
                    </a>
                  ),
                },
              ]}
              data={filteredStatements}
              showPaginate={false}
              showHeader={false}
            />
          </div>
        )}
      </div>
    </>
  );
};
